import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const LOCAL_KEY = 'va_cart_local_v1';

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], count: 0, subtotal: 0, shipping: 0, total: 0 });
  const shippingSettingsRef = React.useRef({ threshold: 1500, shippingCost: 150 });

  // Fetch shipping settings once on mount so unauthenticated cart uses real values
  useEffect(() => {
    api.get('/settings').then((r) => {
      const d = r.data || {};
      shippingSettingsRef.current = {
        threshold: d.free_shipping_threshold != null ? Number(d.free_shipping_threshold) : 1500,
        shippingCost: d.shipping_cost != null ? Number(d.shipping_cost) : 150,
      };
    }).catch(() => {});
  }, []);

  const loadLocal = () => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      const items = raw ? JSON.parse(raw) : [];
      const subtotal = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
      const count = items.reduce((s, i) => s + i.qty, 0);
      const { threshold, shippingCost } = shippingSettingsRef.current;
      const shipping = subtotal >= threshold || subtotal === 0 ? 0 : shippingCost;
      return { items, count, subtotal, shipping, free_shipping_threshold: threshold, total: subtotal + shipping };
    } catch {
      return { items: [], count: 0, subtotal: 0, shipping: 0, free_shipping_threshold: 1500, total: 0 };
    }
  };

  const saveLocal = (items) => localStorage.setItem(LOCAL_KEY, JSON.stringify(items));

  const refresh = useCallback(async () => {
    if (user) {
      try {
        const r = await api.get('/cart');
        setCart(r.data);
        return;
      } catch { /* fall through */ }
    }
    setCart(loadLocal());
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge local cart into server when user logs in
  useEffect(() => {
    const merge = async () => {
      if (!user) return;
      const raw = localStorage.getItem(LOCAL_KEY);
      if (!raw) return;
      try {
        const items = JSON.parse(raw);
        for (const it of items) {
          try {
            await api.post('/cart/items', { product_id: it.product_id, qty: it.qty });
          } catch {}
        }
        localStorage.removeItem(LOCAL_KEY);
        refresh();
      } catch {}
    };
    merge();
  }, [user, refresh]);

  const addItem = async (product, qty = 1) => {
    if (user) {
      const r = await api.post('/cart/items', { product_id: product.product_id, qty });
      setCart(r.data);
      return;
    }
    const raw = localStorage.getItem(LOCAL_KEY);
    const items = raw ? JSON.parse(raw) : [];
    const existing = items.find((i) => i.product_id === product.product_id);
    if (existing) existing.qty += qty;
    else items.push({
      product_id: product.product_id, qty,
      title: product.title, price: product.price, image: product.main_image,
      vendor: product.vendor, handle: product.handle,
    });
    saveLocal(items);
    setCart(loadLocal());
  };

  const updateQty = async (productId, qty) => {
    if (user) {
      const r = await api.patch(`/cart/items/${productId}`, { qty });
      setCart(r.data);
      return;
    }
    const raw = localStorage.getItem(LOCAL_KEY);
    let items = raw ? JSON.parse(raw) : [];
    if (qty <= 0) items = items.filter((i) => i.product_id !== productId);
    else items = items.map((i) => (i.product_id === productId ? { ...i, qty } : i));
    saveLocal(items);
    setCart(loadLocal());
  };

  const removeItem = async (productId) => {
    if (user) {
      const r = await api.delete(`/cart/items/${productId}`);
      setCart(r.data);
      return;
    }
    const raw = localStorage.getItem(LOCAL_KEY);
    const items = (raw ? JSON.parse(raw) : []).filter((i) => i.product_id !== productId);
    saveLocal(items);
    setCart(loadLocal());
  };

  const clear = async () => {
    if (user) {
      await api.delete('/cart');
    }
    localStorage.removeItem(LOCAL_KEY);
    setCart({ items: [], count: 0, subtotal: 0, shipping: 0, total: 0 });
  };

  const value = useMemo(() => ({ cart, refresh, addItem, updateQty, removeItem, clear }), [cart, refresh]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};
